import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import {
    calculateAverageRating,
    calculateTotalSpent,
    getTopValueSessions,
} from '../lib/calculations';
import { formatPrice } from '../lib/constants';
import AppLayout from '../components/layout/AppLayout';
import StatCard from '../components/cards/StatCard';
import SessionCard from '../components/cards/SessionCard';
import TrendChart from '../components/charts/TrendChart';
import StrainTypeChart from '../components/charts/StrainTypeChart';
import type { SessionDetails } from '../types/session';
import {
    Plus,
    Activity,
    CreditCard,
    Diamond,
    Trophy,
    History,
    Sparkles,
    Sprout,
    Zap,
    ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Dashboard() {
    const { user } = useAuth();
    const [sessions, setSessions] = useState<SessionDetails[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user) {
            fetchSessions();
        }
    }, [user]);

    const fetchSessions = async () => {
        if (!user) return;

        const { data, error } = await supabase
            .from('session_details')
            .select('*')
            .eq('user_id', user.id)
            .order('date_used', { ascending: true });

        if (error) {
            console.error('Error fetching sessions:', error);
        } else if (data) {
            setSessions(data as SessionDetails[]);
        }
        setLoading(false);
    };

    // Calculate stats
    const totalSessions = sessions.length;
    const avgRating = calculateAverageRating(
        sessions.map((s) => s.overall_rating)
    );
    const totalSpent = calculateTotalSpent(sessions);
    const avgValueScore = sessions.length > 0
        ? (sessions.reduce((sum, s) => sum + s.value_score, 0) / sessions.length).toFixed(1)
        : 0;

    // Reverse for lists to show latest first
    const reversedSessions = [...sessions].reverse();
    const topSessions = getTopValueSessions(reversedSessions, 3);

    // Prepare chart data
    const trendData = sessions.map(s => ({
        date: s.date_used,
        value: s.value_score,
        rating: s.overall_rating
    }));

    const strainTypeCounts = sessions.reduce((acc: any, s) => {
        const type = s.strain_type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});

    const strainTypeData = Object.entries(strainTypeCounts).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: value as number
    }));

    if (loading) {
        return (
            <AppLayout title="Dashboard">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Activity className="text-accent-purple animate-pulse mb-4" size={48} />
                    <p className="text-dark-muted animate-pulse">Analyzing Session Data...</p>
                </div>
            </AppLayout>
        );
    }

    if (sessions.length === 0) {
        return (
            <AppLayout title="Dashboard">
                <div className="space-y-8 animate-in fade-in duration-700">
                    <div className="card text-center py-16 bg-dark-surface/50 overflow-hidden relative border-accent-purple/10">
                        {/* Decorative background element */}
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-accent-purple/10 blur-[100px] rounded-full pointer-events-none" />

                        <div className="relative inline-block mb-8">
                            <div className="absolute inset-0 bg-accent-purple/20 blur-2xl rounded-full scale-150 animate-pulse-slow" />
                            <div className="relative bg-dark-surface p-6 rounded-3xl border border-accent-purple/30 shadow-2xl shadow-accent-purple/20">
                                <Sprout size={56} className="text-accent-purple" />
                            </div>
                        </div>

                        <h2 className="text-3xl font-bold mb-3 tracking-tight">Your Dashboard Awaits</h2>
                        <p className="text-dark-muted mb-10 max-w-sm mx-auto leading-relaxed">
                            Start tracking your strains and sessions to unlock personalized value insights and usage trends.
                        </p>

                        <div className="flex flex-col gap-4 max-w-xs mx-auto">
                            <Link to="/new-session" className="btn-primary py-4 text-lg shadow-xl shadow-accent-purple/25 flex items-center justify-center gap-2">
                                <Plus size={20} strokeWidth={3} /> Log First Session
                            </Link>
                            <div className="grid grid-cols-2 gap-3">
                                <Link to="/brands" className="text-xs font-bold uppercase tracking-wider py-4 px-4 bg-dark-bg border border-dark-border rounded-2xl hover:border-accent-purple/50 transition-all flex items-center justify-center gap-2 group">
                                    Brands <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </Link>
                                <Link to="/products" className="text-xs font-bold uppercase tracking-wider py-4 px-4 bg-dark-bg border border-dark-border rounded-2xl hover:border-accent-purple/50 transition-all flex items-center justify-center gap-2 group">
                                    Products <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="card bg-dark-surface/20 border-dark-border/50">
                        <div className="flex items-center gap-2 mb-6 text-accent-purple/60">
                            <Zap size={18} />
                            <h3 className="text-xs font-bold uppercase tracking-[0.2em]">Quick Setup Guide</h3>
                        </div>
                        <div className="space-y-6">
                            {[
                                { step: 1, title: 'Register Brands', desc: 'Add the brands you buy from to organize your registry.', path: '/brands' },
                                { step: 2, title: 'Add Products', desc: 'Link purchased products to their brands and strains.', path: '/products' },
                                { step: 3, title: 'Track Sessions', desc: 'Log effects and cost to unlock personalized value ratings.', path: '/new-session' },
                            ].map((item) => (
                                <Link to={item.path} key={item.step} className="flex gap-5 group items-start">
                                    <div className="w-10 h-10 rounded-2xl bg-dark-surface border border-dark-border flex items-center justify-center shrink-0 font-black text-dark-muted group-hover:bg-accent-purple/10 group-hover:text-accent-purple group-hover:border-accent-purple/30 transition-all">
                                        {item.step}
                                    </div>
                                    <div>
                                        <p className="font-bold text-sm tracking-tight mb-1 group-hover:text-dark-text transition-colors">{item.title}</p>
                                        <p className="text-xs text-dark-muted leading-relaxed">{item.desc}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout title="Dashboard">
            <div className="space-y-8 pb-10">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight">Activity Overview</h2>
                        <p className="text-xs text-dark-muted font-medium mt-1">
                            Welcome back, logging your progress keeps insights accurate.
                        </p>
                    </div>
                    <Link to="/new-session" className="bg-accent-purple border border-accent-purple shadow-lg shadow-accent-purple/20 text-white w-12 h-12 rounded-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all">
                        <Plus size={24} strokeWidth={2.5} />
                    </Link>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                    <StatCard
                        icon={<Activity size={24} className="text-accent-purple" />}
                        label="Total Sessions"
                        value={totalSessions}
                        subtext={`Rating: ${avgRating}/10`}
                        className="bg-accent-purple/5"
                    />
                    <StatCard
                        icon={<CreditCard size={24} className="text-emerald-500" />}
                        label="Investment"
                        value={formatPrice(totalSpent)}
                        subtext={`Avg: ${formatPrice(totalSpent / totalSessions)}`}
                    />
                    <StatCard
                        icon={<Diamond size={24} className="text-amber-500" />}
                        label="Average Value Score"
                        value={avgValueScore}
                        subtext="Price-to-effect efficiency"
                        className="col-span-2 bg-gradient-to-r from-dark-surface to-dark-bg border-amber-500/20"
                    />
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="card bg-dark-surface/40">
                        <div className="flex items-center gap-2 mb-6">
                            <Activity size={16} className="text-accent-purple/60" />
                            <h3 className="text-[10px] font-bold text-dark-muted uppercase tracking-[0.2em]">
                                Value Trend
                            </h3>
                        </div>
                        <TrendChart data={trendData} />
                    </div>
                    <div className="card bg-dark-surface/40">
                        <div className="flex items-center gap-2 mb-6">
                            <Sprout size={16} className="text-accent-purple/60" />
                            <h3 className="text-[10px] font-bold text-dark-muted uppercase tracking-[0.2em]">
                                Genetic Reach
                            </h3>
                        </div>
                        <StrainTypeChart data={strainTypeData} />
                    </div>
                </div>

                {/* Top Value Sessions */}
                <div>
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                            <Trophy size={18} className="text-amber-500/80" />
                            <h2 className="text-xl font-bold tracking-tight">Best Value</h2>
                        </div>
                        <Link to="/compare" className="text-[10px] font-black uppercase tracking-widest text-accent-purple hover:bg-accent-purple/10 px-3 py-1.5 rounded-lg transition-all">
                            Insights Report
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {topSessions.map((session) => (
                            <SessionCard key={session.id} session={session} />
                        ))}
                    </div>
                </div>

                {/* Recent Sessions */}
                <div>
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                            <History size={18} className="text-accent-purple/60" />
                            <h2 className="text-xl font-bold tracking-tight">Recent Logs</h2>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {reversedSessions.slice(0, 5).map((session) => (
                            <SessionCard key={session.id} session={session} />
                        ))}
                    </div>

                    {sessions.length > 5 && (
                        <div className="mt-8 pt-6 border-t border-dark-border text-center">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-dark-muted">
                                Managing {sessions.length} archived entries
                            </p>
                        </div>
                    )}
                </div>

                {/* Quick Insights */}
                <div className="card bg-gradient-to-br from-accent-purple/10 via-dark-surface to-dark-surface border-accent-purple/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 text-accent-purple/5 group-hover:scale-150 transition-transform duration-700">
                        <Sparkles size={120} />
                    </div>
                    <div className="relative">
                        <div className="flex items-center gap-2 mb-4">
                            <Sparkles size={18} className="text-accent-purple" />
                            <h3 className="text-xs font-black uppercase tracking-[0.2em]">Smart Summary</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wider mb-1">Top Lineage</p>
                                <p className="font-bold text-lg capitalize tracking-tight">
                                    {strainTypeData.length > 0 ? strainTypeData.sort((a, b) => b.value - a.value)[0]?.name : 'N/A'}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wider mb-1">Avg Session</p>
                                <p className="font-bold text-lg tracking-tight">
                                    {Math.round(
                                        sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) /
                                        sessions.length
                                    )} min
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
